import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiLoader, FiMusic, FiDollarSign } = FiIcons;

const inputCls = 'w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors';
const GENRES = ['Electronic', 'Hip-Hop', 'R&B', 'Pop', 'Rock', 'Jazz', 'Classical', 'Ambient', 'House', 'Techno', 'Drum & Bass', 'Other'];
const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</label>
    {children}
  </div>
);

const EditDropModal = ({ drop, onClose, onSave }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (drop) setForm({
      title: drop.title || '', description: drop.description || '',
      genre: drop.genre || '', audio_url: drop.audio_url || '',
      preview_url: drop.preview_url || '', cover_url: drop.cover_url || '',
      min_price: String(drop.min_price ?? 0), suggested_price: String(drop.suggested_price ?? 5),
      tags: (drop.tags || []).join(', '),
    });
  }, [drop]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Title required.'); return; }
    setLoading(true); setErr('');
    const { error } = await onSave(drop.id, {
      ...form,
      min_price: parseFloat(form.min_price) || 0,
      suggested_price: parseFloat(form.suggested_price) || 5,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    });
    setLoading(false);
    if (error) setErr(error.message);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-8 py-6 border-b border-zinc-900">
          <h3 className="text-2xl font-black text-white uppercase tracking-tight">Edit Drop</h3>
          <button onClick={onClose} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white transition-all">
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>
        <div className="p-8 space-y-5 max-h-[65vh] overflow-y-auto">
          <Field label="Title *">
            <input className={inputCls} value={form.title || ''} onChange={e => set('title', e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea className={`${inputCls} resize-none`} rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Genre">
              <select className={inputCls} value={form.genre || ''} onChange={e => set('genre', e.target.value)}>
                <option value="">Select...</option>
                {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Tags (comma separated)">
              <input className={inputCls} value={form.tags || ''} onChange={e => set('tags', e.target.value)} />
            </Field>
          </div>
          <Field label="Cover Art URL">
            <input className={inputCls} placeholder="https://..." value={form.cover_url || ''} onChange={e => set('cover_url', e.target.value)} />
          </Field>
          <Field label="Audio URL *">
            <div className="relative">
              <SafeIcon icon={FiMusic} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
              <input className={`${inputCls} pl-10`} value={form.audio_url || ''} onChange={e => set('audio_url', e.target.value)} />
            </div>
          </Field>
          <Field label="Preview URL">
            <input className={inputCls} value={form.preview_url || ''} onChange={e => set('preview_url', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Min Price (USD)">
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                <input type="number" min="0" step="0.01" className={`${inputCls} pl-9`} value={form.min_price || '0'} onChange={e => set('min_price', e.target.value)} />
              </div>
            </Field>
            <Field label="Suggested Price (USD)">
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                <input type="number" min="0" step="0.01" className={`${inputCls} pl-9`} value={form.suggested_price || '5'} onChange={e => set('suggested_price', e.target.value)} />
              </div>
            </Field>
          </div>
          {err && <p className="text-red-400 text-xs font-black">{err}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-zinc-900">
          <button onClick={onClose} className="px-5 py-3 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-600 hover:text-white transition-all">Cancel</button>
          <button onClick={handleSave} disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-black uppercase tracking-widest rounded-2xl hover:scale-105 transition-all disabled:opacity-50">
            {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDropModal;